import axios from 'axios'
import type { AssessmentCompletionResponse, ChatResponse, FnaFormData } from '../types'
import { HTTP_ENDPOINTS } from '../contracts/endpoints'
import { inferApiBaseUrl, resolveApiBaseUrl } from './urls'

export { inferApiBaseUrl }

export const API_BASE_URL = resolveApiBaseUrl(import.meta.env.VITE_API_BASE_URL)

axios.defaults.withCredentials = true

const logApiError = (action: string, error: unknown, context: Record<string, unknown> = {}): void => {
  console.error({
    message: 'Frontend API request failed',
    error: error instanceof Error
      ? { message: error.message, stack: error.stack }
      : { message: String(error) },
    context: {
      action,
      ...context,
    },
  })
}

// Create axios instance with proper configuration
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
})

/**
 * Gets CSRF token from the Django backend.
 * 
 * @returns Promise<string> The CSRF token
 */
export const getCSRFToken = async (): Promise<string> => {
  try {
    const response = await api.get('/api/csrf/')
    return response.data.csrfToken
  } catch (error) {
    logApiError('getCSRFToken', error)
    // Return empty string if CSRF fails - let backend handle it
    return ''
  }
}

/**
 * Sends a text message to the chat endpoint.
 * 
 * @param message - The message to send
 * @param formData - Optional form data to include
 * @returns Promise<ChatResponse> The chat response
 */
export const sendChatMessage = async (
  message: string, 
  formData?: FnaFormData
): Promise<ChatResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    
    const response = await api.post(HTTP_ENDPOINTS.chat, {
      message,
      formdata: formData || {},
    }, {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      }
    })

    return response.data
  } catch (error) {
    logApiError('sendChatMessage', error, { hasFormData: Boolean(formData) })
    throw error
  }
}

/**
 * Sends audio data to the backend for transcription and processing.
 * 
 * @param audioBlob - The recorded audio data
 * @param formData - Optional form data to include
 * @returns Promise<ChatResponse> The processed response
 */
export const sendAudioMessage = async (
  audioBlob: Blob, 
  formData?: FnaFormData
): Promise<ChatResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    
    const uploadFormData = new FormData()
    uploadFormData.append('audio', audioBlob, 'audio.webm')
    
    if (formData) {
      uploadFormData.append('formdata', JSON.stringify(formData))
    }

    const response = await api.post(HTTP_ENDPOINTS.audio, uploadFormData, {
      headers: {
        'X-CSRFToken': csrfToken,
      }
    })

    return response.data
  } catch (error) {
    logApiError('sendAudioMessage', error, { hasFormData: Boolean(formData) })
    throw error
  }
}

/**
 * Persists validated assessment data and returns summary context for follow-up tools.
 *
 * @param formData - The form data to persist
 * @returns Promise<AssessmentCompletionResponse> Saved assessment metadata and summary
 */
export const completeAssessment = async (
  formData: FnaFormData
): Promise<AssessmentCompletionResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post<AssessmentCompletionResponse>(
      HTTP_ENDPOINTS.fnaComplete,
      JSON.stringify(formData),
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'Accept': 'application/json',
        },
        timeout: 30000,
      }
    )
    return response.data
  } catch (error) {
    logApiError('completeAssessment', error)
    throw error
  }
}

// ============================================================================
// Plaid Integration API
// ============================================================================

export interface PlaidAccount {
  plaid_item_id?: number
  item_id?: string | null
  institution_name?: string | null
  name: string
  mask: string | null
  type: string
  subtype: string
  balances: {
    available: number | null
    current: number | null
    limit: number | null
  }
}

export interface PlaidInstitution {
  plaid_item_id: number
  item_id?: string | null
  institution_id?: string | null
  institution_name?: string | null
  connected: boolean
  status: string
  needs_reconnection: boolean
  last_error_code?: string | null
  last_successful_sync?: string | null
  account_count: number
  total_balance?: number | null
  accounts: PlaidAccount[]
  error?: string
  error_code?: string | null
  message?: string
}

export interface PlaidPartialError {
  plaid_item_id: number
  institution_name?: string | null
  error?: string
  error_code?: string | null
  message?: string
}

export interface LinkedAccountsResponse {
  connected: boolean
  status: string
  institution_name?: string | null
  item_id?: string | null
  plaid_item_id?: number | null
  needs_reconnection: boolean
  last_error_code?: string | null
  last_successful_sync?: string | null
  institution_count: number
  account_count: number
  total_balance?: number | null
  accounts: PlaidAccount[]
  institutions: PlaidInstitution[]
  partial_errors: PlaidPartialError[]
  data_source?: string
  message?: string
  error?: string
  code?: string
}

export interface PlaidLinkTokenResponse {
  link_token: string
}

export interface PlaidConnectionStatusResponse {
  connected: boolean
  status: string
  institution_name?: string | null
  item_id?: string | null
  plaid_item_id?: number | null
  needs_reconnection: boolean
  last_error_code?: string | null
  last_successful_sync?: string | null
  institution_count?: number
  account_count?: number
  accounts?: PlaidAccount[]
  institutions?: PlaidInstitution[]
  partial_errors?: PlaidPartialError[]
  message?: string
}

export interface PlaidLinkTokenRequest {
  mode?: 'create' | 'update'
  plaid_item_id?: number
}

/**
 * Creates a Plaid Link token for initiating the account linking flow.
 * 
 * @returns Promise<string> The link token
 */
export const createPlaidLinkToken = async (
  payload: PlaidLinkTokenRequest = {}
): Promise<string> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post<PlaidLinkTokenResponse>(HTTP_ENDPOINTS.plaidLinkToken, payload, {
      headers: {
        'X-CSRFToken': csrfToken,
      }
    })
    return response.data.link_token
  } catch (error) {
    logApiError('createPlaidLinkToken', error)
    throw error
  }
}

/**
 * Exchanges a Plaid public token for a non-secret connection status payload.
 * 
 * @param publicToken - The public token from Plaid Link
 * @returns Promise<PlaidConnectionStatusResponse> The connection status response
 */
export const exchangePlaidPublicToken = async (
  publicToken: string,
  payload: {
    plaid_item_id?: number
    institution_name?: string
    institution_id?: string
  } = {}
): Promise<PlaidConnectionStatusResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post<PlaidConnectionStatusResponse>(
      HTTP_ENDPOINTS.plaidExchangePublicToken,
      { public_token: publicToken, ...payload },
      {
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        }
      }
    )
    return response.data
  } catch (error) {
    logApiError('exchangePlaidPublicToken', error)
    throw error
  }
}

/**
 * Gets the user's linked bank accounts from cached balances (fast).
 *
 * @returns Promise<LinkedAccountsResponse> The linked accounts data
 */
export const getLinkedAccounts = async (): Promise<LinkedAccountsResponse> => {
  try {
    const response = await api.get<LinkedAccountsResponse>(HTTP_ENDPOINTS.linkedAccounts)
    return response.data
  } catch (error) {
    logApiError('getLinkedAccounts', error)
    throw error
  }
}

/**
 * Refreshes live balances from Plaid and returns the updated overview. This
 * runs off the initial-render path (the page shows cached balances first), and
 * it refreshes every institution serially, so allow extra timeout headroom.
 *
 * @returns Promise<LinkedAccountsResponse> The refreshed linked accounts data
 */
export const refreshLinkedAccounts = async (): Promise<LinkedAccountsResponse> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post<LinkedAccountsResponse>(
      HTTP_ENDPOINTS.linkedAccountsRefresh,
      {},
      {
        headers: {
          'X-CSRFToken': csrfToken,
        },
        timeout: 30000,
      }
    )
    return response.data
  } catch (error) {
    logApiError('refreshLinkedAccounts', error)
    throw error
  }
}

/**
 * Disconnects the user's Plaid connection.
 * 
 * @returns Promise<{ success: boolean; message: string }> The disconnect result
 */
export const disconnectPlaid = async (
  plaidItemId?: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const csrfToken = await getCSRFToken()
    const response = await api.post(
      HTTP_ENDPOINTS.disconnectPlaid,
      plaidItemId ? { plaid_item_id: plaidItemId } : {},
      {
        headers: {
          'X-CSRFToken': csrfToken,
        }
      }
    )
    return response.data
  } catch (error) {
    logApiError('disconnectPlaid', error)
    throw error
  }
}
